import { bind } from 'bind-event-listener';
import { CEXP_LOADED } from '../../../common/constants';
import { sendProviderLoadedMessageToParent } from '../../../common/utils/messages';
/**
 * Checks if the client is loaded via the SDK.
 *
 * This function determines whether the current window is embedded within an SDK by checking
 * if it is not the top window and if a specific flag (`window.cexp_sdk`) is set to a boolean value.
 * If the flag is not set, it listens for a handshake message from the parent window for a specified
 * timeout period. If the message is received within the timeout, the function resolves to `true`,
 * indicating that the client is loaded via the SDK. Otherwise, it resolves to `false`.
 *
 * The first time this function is called, it could take up to the duration of the timeout to resolve.
 * Subsequent calls should be instant (although still asynchronous) as the result is cached in `window.cexp_sdk`.
 *
 * @param {GetIsClientLoadedViaSdkOptions} [options={ timeout: 2000 }] - Optional configuration options, including the timeout period in milliseconds.
 * @returns {Promise<boolean>} A promise that resolves to a boolean indicating whether the client is loaded via the SDK.
 */

export function getIsClientLoadedViaSdk() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    timeout: 2000
  };
  // If we are the top window we are not inside the SDK
  if (window === window.top) {
    return Promise.resolve(false);
  }
  if (typeof window.cexp_sdk === 'boolean') {
    return Promise.resolve(window.cexp_sdk);
  }
  return new Promise(function (resolve) {
    // After specified timeout return false
    var timeout = setTimeout(function () {
      // We set the window value for cacheing future checks
      window.cexp_sdk = false;
      resolve(false);
    }, options.timeout);

    // Listen for the parent to send a handshake message
    bind(window, {
      type: 'message',
      listener: function listener(event) {
        if (event.data === CEXP_LOADED) {
          clearTimeout(timeout);
          // We set the window value for cacheing future checks
          window.cexp_sdk = true;
          resolve(true);
        }
      }
    });

    // Indicate to parent we are ready to receive messages
    sendProviderLoadedMessageToParent();
  });
}